<template>
  <div class="project" @click="onClick">
    <ButtonEditCard :class="['project__edit', { listView: showListView }]">
      <template #option>
        <span class="popup__option" @click="showProjectModal(data.id)">{{
          $t('shared.button.move_to_folder')
        }}</span>
      </template>
    </ButtonEditCard>
    <router-link
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="project__header"
    >
      <span class="header__label">{{ data.attributes.identifier }}</span>
      <span class="header__text">{{ data.attributes.name }}</span>
    </router-link>
    <router-link
      v-if="!showListView"
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="service body__column"
    >
      <span class="service__label column__label"
        >{{ $t('shared.label.workflow') }}:</span
      >
      <span ref="service" class="service__text column__text">
        {{ data.attributes.workflow.attributes.display_name }}</span
      >
    </router-link>
    <router-link
      v-if="!showListView"
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="price body__column"
    >
      <span class="price__label column__label"
        >{{ $t('shared.label.price_without_vat') }}:</span
      >
      <span class="price__text column__text">{{
        formatPrice(
          data.attributes.price.amount,
          data.attributes.price.currency
        )
      }}</span>
    </router-link>
    <router-link
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="date body__column"
    >
      <span v-if="!showListView" class="date__label column__label"
        >{{ $t('shared.label.date') }}:</span
      >
      <span class="date__text column__text">{{
        formatDate(data.attributes.created_at)
      }}</span>
    </router-link>
    <router-link
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="from body__column"
    >
      <span v-if="!showListView" class="from__label column__label"
        >{{
          data.attributes.workflow.attributes.identifier === 'copyediting'
            ? $t('shared.label.languages')
            : $t('shared.label.from')
        }}:</span
      >
      <span class="from__text column__text">{{
        data.attributes.workflow.attributes.identifier !== 'copyediting'
          ? data.attributes.source_language
          : targetList
      }}</span>
    </router-link>
    <router-link
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="to body__column"
    >
      <span
        v-if="
          !showListView &&
          data.attributes.workflow.attributes.identifier !== 'copyediting'
        "
        class="to__label column__label"
        >{{ $t('shared.label.to') }}:</span
      >
      <span
        v-if="data.attributes.workflow.attributes.identifier !== 'copyediting'"
        class="to__text column__text"
        >{{ targetList }}</span
      >
    </router-link>
    <router-link
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="deadline body__column"
    >
      <span v-if="!showListView" class="deadline__label column__label"
        >{{ $t('shared.label.preferred_deadline') }}:</span
      >
      <span class="deadline__text column__text">{{
        formatDeadline(data.attributes.preferred_deadline)
      }}</span>
    </router-link>
    <router-link
      :to="{ name: 'clientProject', params: { id: data.id } }"
      class="project__footer"
    >
      <div class="task__progress">
        <span ref="text" class="progress__text">{{ status.text }}</span>
      </div>
    </router-link>
  </div>
</template>
<script setup>
import { ProjectService } from '@/api/new/services/project.service'
import ButtonEditCard from '@/components/shared/ButtonEditCard'
import moment from 'moment'
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

const store = useStore()
const router = useRouter()

const text = ref(null)
const service = ref(null)

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  }
})

const showListView = computed(() => store.state.account.showListView)

const setProjectId = (payload) =>
  store.commit('workspace/setProjectId', payload)
const toggleSelectFolderModal = () =>
  store.commit('modals/toggleSelectFolderModal')

const status = computed(() => {
  return ProjectService.getStatus(props.data.attributes.status)
})

const targetList = computed(() => {
  return props.data.attributes.target_languages.join(', ')
})

function onClick(e) {
  if (e.target.classList.contains('project')) {
    router.push({
      name: 'clientProject',
      params: { id: props.data.id }
    })
  }
}

function showProjectModal(id) {
  toggleSelectFolderModal()
  setProjectId(id)
}

function formatPrice(price, currency) {
  if (price === 0) return '-'
  return `${price / 100} ${currency}`
}

function formatDate(date) {
  if (!date) return '-'
  return moment(date).format('YYYY-MM-DD')
}

function formatDeadline(date) {
  if (!date) return '-'
  return moment(date).format('YYYY-MM-DD HH:mm')
}

watch(
  () => props.data,
  () => {
    nextTick(() => {
      if (text.value) {
        text.value.style.setProperty(
          '--status-primary',
          status.value.colors.primary
        )
        text.value.style.setProperty(
          '--status-secondary',
          status.value.colors.secondary
        )
      }
    })
  },
  { deep: true, immediate: true }
)

watch(
  () => showListView.value,
  (isListView) => {
    if (!isListView) {
      nextTick(() => {
        service?.value?.style.setProperty(
          '--status-primary',
          status.value.colors.primary
        )
      })
    }
  },
  { immediate: true }
)
</script>
<style lang="scss" scoped>
.project {
  position: relative;
  display: grid;
  grid-template-rows: auto min-content min-content min-content auto;
  grid-template-columns: 1fr 1fr;
  height: auto;
  width: 100%;
  box-shadow: 0px 2px 5px #15174a0d;
  background: $white;
  padding: 20px;
  border-radius: 10px;
  cursor: pointer;
  grid-row-gap: 10px;
  grid-template-areas:
    'projectHeader projectHeader '
    'projectService projectService'
    'projectPrice projectDate'
    'projectFrom projectTo'
    'projectDeadline projectDeadline'
    'projectFooter projectFooter';

  &__edit {
    opacity: 0;
    transition: opacity 100ms ease-in-out;
    will-change: opacity;
  }
  &:hover {
    .project__edit {
      opacity: 1;
    }
  }
  .project__edit {
    &.listView {
      position: relative;
      top: 0;
      right: 10px;
      align-self: center;
    }
  }
  &__header {
    display: flex;
    flex-direction: column;
    grid-area: projectHeader;
  }
  &__footer {
    padding: 15px 0 0 0;
    grid-area: projectFooter;
    border-top: 1px solid #e6e9f0;
  }
}

.header {
  &__label {
    color: $black-100;
    font-weight: 600;
    padding-right: 20px;
  }
  &__text {
    font-size: 12px;
    color: $black-80;
    @include tabletPortrait {
      font-size: 14px;
    }
  }
}

.body__column {
  display: flex;
  flex-direction: column;
}

.column {
  &__label {
    font-size: 12px;
    color: $black-80;
    padding-bottom: 5px;
    @include tabletPortrait {
      font-size: 14px;
    }
  }
  &__text {
    font-size: 12px;
    color: $black-100;
    background: $blue-10;
    border-radius: var(--border-radius);
    width: fit-content;
    padding: 1px 10px;
    @include tabletPortrait {
      font-size: 14px;
    }
  }
}

.service {
  grid-area: projectService;
  grid-column: 1 / -1;
  border-top: 1px solid #e6e9f0;
  padding-top: 10px;
  &__text {
    display: flex;
    align-items: center;
    &:before {
      content: '';
      height: 10px;
      width: 10px;
      background: var(--status-primary);
      display: block;
      border-radius: var(--border-radius);
      margin-right: 5px;
    }
  }
}

.price,
.date {
  grid-row: 2 / 3;
}

.price {
  grid-area: projectPrice;
  grid-column: 1 / 2;
}

.date {
  grid-area: projectDate;
  grid-column: 2 / -1;
}

.to,
.from {
  grid-row: 3 / 4;
  &__text {
    text-transform: uppercase;
  }
}

.to {
  grid-area: projectTo;
  grid-column: 2 / -1;
}

.from {
  grid-area: projectFrom;
  grid-column: 1 / 1;
}

.deadline {
  grid-area: projectDeadline;
  grid-column: 1 / 1;
}

.footer__column {
  display: flex;
  padding-bottom: 10px;
  color: $black-80;
  .task__count {
    display: block;
    margin-left: auto;
    color: $black-100;
    font-size: 12px;
    font-weight: 500;
    @include tabletPortrait {
      font-size: 14px;
    }
  }
  .task__label {
    font-size: 14px;
  }
  .task__icon {
    width: auto !important;
    height: auto;
    color: $black-50;
  }
}

.task__progress {
  .progress__text {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    color: var(--status-primary);
    background: var(--status-secondary);
    padding: 5px;
    border-radius: 5px;
    font-size: 14px;
    @include tabletPortrait {
      font-size: 16px;
    }
  }
}

.grid-container--list-view {
  .project {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(10, 1fr) 20px;
    grid-template-areas: 'projectHeader projectHeader projectDate projectDeadline projectDeadline projectTo projectFrom projectFooter projectFooter';

    grid-column-gap: 20px;
    &__header {
      padding: 0;
      grid-column: 1 / span 3;
    }
    &__body {
      padding: 0;
      grid-row-gap: 0;
      border: none;
    }
    &__footer {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0;
      border: none;
    }
    .header__text,
    .header__label {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      width: 100%;
      max-width: 200px;
      @include desktopLarge {
        max-width: 400px;
      }
    }
  }

  .date {
    grid-column: 4 / 4;
    grid-row-start: 1;
    grid-row-end: 1;
  }

  .deadline {
    grid-column: 5 / span 2;
    grid-row-start: 1;
    grid-row-end: 2;
  }

  .from {
    grid-column: 7 / 7;
    grid-row-start: 1;
    grid-row-end: 1;
  }

  .to {
    grid-column: 8 / 8;
    grid-row-start: 1;
    grid-row-end: 1;
  }

  .date,
  .to,
  .from,
  .deadline {
    display: flex;
    justify-content: center;
    font-weight: 500;
  }

  .project__footer {
    grid-column: 9 / span 2;
    grid-row: 1 / 1;
  }

  .task__progress {
    width: 100%;
    .progress__text {
      font-size: 14px;
      font-weight: 500;
      text-align: center;
    }
  }

  .column {
    &__label {
      padding-bottom: 0;
    }
  }
}
</style>
